import { Format } from "@common/_constants";
import LocalTime from "@common/components/Atoms/LocalTime";
import RelodeRep from "@common/components/Atoms/RelodeRep";
import moment from "moment";
import * as React from "react";
import { Link } from "react-router-dom";
import { Sticky, StickyContainer } from "react-sticky";

// small screen page-level actions
interface DetailActionsProps {
    openEditReferralModal: () => void;
    linkName: string;
}

export const DetailActions: React.SFC<DetailActionsProps> = (props: DetailActionsProps) => (
    <ul className="c-detail__actions">
        {props.openEditReferralModal && (
            <li>
                <a className="c-link c-link--magenta" onClick={props.openEditReferralModal}>
                    {props.linkName}
                </a>
            </li>
        )}
    </ul>
);

interface DetailSummaryProps {
    candidate: {
        job_id: number;
        job_name: string;
        status_name: string;
        hired_timestamp: string;
        date_applied: string;
        user_manager_profile_image_url: string;
        user_manager_name: string;
        user_manager_phone_number: string;
        user_manager_phone_number_for_url: string;
        closest_interview_start_time: string;
        closest_interview_status_type_id: number;
    };
    detailSummaryActions: React.ReactNode;
}

// large screen summary box
export const DetailSummary: React.SFC<DetailSummaryProps> = (props: DetailSummaryProps) => {
    const shortLength = 45;
    const longLength = 50;

    const jobNameLength = props.candidate.job_name.length;
    const detailSummaryTitleClassNames = ["c-detail-summary__title"];
    if (jobNameLength >= shortLength) {
        detailSummaryTitleClassNames.push(
            jobNameLength <= longLength
                ? "c-detail-summary__title--smaller"
                : "c-detail-summary__title--smallest"
        );
    }

    return (
        <StickyContainer className="c-detail__sidebar-stickycontainer">
            <Sticky topOffset={0} disableCompensation={true}>
                {({ style, isSticky }) => {
                    return (
                        <section
                            className="c-detail-summary"
                            style={{ ...style, marginTop: isSticky ? "100px" : "0" }}>
                            <div className="c-detail-summary__header">
                                <h3 className={detailSummaryTitleClassNames.join(" ")}>
                                    <Link
                                        to={"/jobs/" + props.candidate.job_id}
                                        className="c-link c-link--light-text">
                                        {props.candidate.job_name}
                                    </Link>
                                </h3>
                            </div>
                            <div className="c-detail-summary__body">
                                <div className="c-detail-summary__block">
                                    <h4 className="c-detail-summary__block-title">stage</h4>
                                    <div className="c-detail-summary__block-value">
                                        {" "}
                                        {props.candidate.status_name}{" "}
                                    </div>
                                </div>

                                {!!props.candidate.closest_interview_start_time && (
                                    <div className="c-detail-summary__block">
                                        <h4 className="c-detail-summary__block-title">interview</h4>
                                        <div className="c-detail-summary__block-value">
                                            {props.candidate.closest_interview_start_time}
                                            {props.candidate.closest_interview_status_type_id === 10
                                                ? " (cancelled)"
                                                : ""}
                                        </div>
                                    </div>
                                )}

                                {props.candidate.hired_timestamp && (
                                    <div className="c-detail-summary__block">
                                        <h4 className="c-detail-summary__block-title">
                                            {moment
                                                .utc()
                                                .diff(moment(props.candidate.hired_timestamp)) > 0
                                                ? "started"
                                                : "starting"}
                                        </h4>
                                        <div className="c-detail-summary__block-value">
                                            <LocalTime
                                                utcTimestamp={props.candidate.hired_timestamp}
                                                format={Format.Datetime.DATE_ABBR_MONTH}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="c-detail-summary__block">
                                    <h4 className="c-detail-summary__block-title">applied</h4>
                                    <div className="c-detail-summary__block-value">
                                        <LocalTime utcTimestamp={props.candidate.date_applied} />
                                    </div>
                                </div>

                                <div className="c-detail-summary__block">
                                    <h4 className="c-detail-summary__block-title">questions?</h4>
                                    <RelodeRep
                                        image={props.candidate.user_manager_profile_image_url}
                                        name={props.candidate.user_manager_name}
                                    />
                                </div>
                            </div>
                            {props.detailSummaryActions}
                        </section>
                    );
                }}
            </Sticky>
        </StickyContainer>
    );
};

interface DetailSummaryActionsProps {
    openEditReferralModal: () => void;
    openMessageModal?: () => void;
    editModalLinkText?: string;
    hideSendMessage?: boolean;
}

export const DetailSummaryActions: React.SFC<DetailSummaryActionsProps> = (
    props: DetailSummaryActionsProps
) => (
    <div className="c-detail-summary__footer">
        {props.openEditReferralModal && (
            <a
                className="c-link c-link--magenta no-underline"
                onClick={props.openEditReferralModal}>
                {props.editModalLinkText || "edit referral"}
            </a>
        )}
        {props.hideSendMessage !== true && (
            <a className="c-link c-link--magenta no-underline" onClick={props.openMessageModal}>
                send message
            </a>
        )}
    </div>
);
